import React, {createContext, useContext, useEffect, useState} from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({children}) => {
    const [isLoggedIn, setIsLoggedIn] = useState(window.__INITIAL_STATE__?.isLoggedIn || false);
    const [isDebug, setIsDebug] = useState(window.__INITIAL_STATE__?.isDebug || false);
    const [allowedAdminEmails, setAllowedAdminEmails] = useState(window.__INITIAL_STATE__?.allowedAdminEmails || []);
    const [anonymousUser, setAnonymousUser] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [userId, setUserId] = useState('');
    const [userRole, setUserRole] = useState('consumer');

    // Configure axios to always include credentials
    useEffect(() => {
        // This ensures cookies are sent with every request
        axios.defaults.withCredentials = true;
        
        // Check if we're already logged in by making a request to check auth status
        checkAuthStatus();
    }, []);
    
    // Function to check authentication status
    const checkAuthStatus = async () => {
        try {
            // Use the lightweight hasaccess endpoint to check auth status and has access to private page
            await axios.get('hasAccess');
            
            // If we get here without an error, the user is logged in
            setIsLoggedIn(true);
            
            // Always fetch user details if authentication is successful
            try {
                const userResponse = await axios.get('userDetails');
                if (userResponse.data) {
                    setUserEmail(userResponse.data.email || '');
                    setUserId(userResponse.data.id || '');
                    setUserRole(userResponse.data.role || 'consumer');
                }
            } catch (userError) {
                console.error('Error fetching user details:', userError);
            }
        } catch (error) {
            console.error('Not logged in or session expired');
            setIsLoggedIn(false);
            setUserEmail('');
            setUserId('');
            setUserRole('consumer');
        }
    };

    const logoutCall = async () => {
        try {
            const response = await axios.get('logout', {})

            if (response.status !== 200) {
                console.error('Logout failed');
            }
        } catch (error) {
            console.error('Logout failed with error:', error);
        }
    }

    const login = async (email, password, appAuthToken) => {
        const encodedCredentials = btoa(`${email}:${password}`);

        try {
            const headers = {
                'Authorization': `Basic ${encodedCredentials}`,
            }

            if (appAuthToken != null) { // This checks for both null and undefined
                headers['X-App-Authorization-Token'] = appAuthToken;
            }

            // The JWT token will be set as an HTTP-only cookie by the server
            // We just need to make sure withCredentials is true, which is set globally
            const response = await axios.get('login', { headers })

            if (response.status !== 200) {
                console.error('Signin failed');
                return
            }

            // Extract user info from response
            // The token is stored in an HTTP-only cookie, so we don't need to handle it here
            const { email: userEmail, id: userId } = response.data;
            
            // Update state with user info
            if (userEmail) setUserEmail(userEmail);
            if (userId) setUserId(userId);
            try {
                const userResponse = await axios.get('userDetails');
                if (userResponse.data) {
                    setUserRole(userResponse.data.role || 'consumer');
                }
            } catch (userError) {
                console.error('Error fetching user details:', userError);
                setUserRole('consumer');
            }
            
            setIsLoggedIn(true);
            setAnonymousUser(false);

        } catch (error) {
            console.error('Signin error:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            // Call the logout endpoint which will clear the HTTP-only cookie
            await logoutCall();
            
            // Update state
            setIsLoggedIn(false);
            setAnonymousUser(false);
            setUserEmail('');
            setUserId('');
            setUserRole('consumer');
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const checkIfLoggedIn = async () => {
        setAnonymousUser(false);

        // Use the same checkAuthStatus function to avoid code duplication
        await checkAuthStatus();
    }

    return (
        <AuthContext.Provider value={{
            checkIfLoggedIn,
            anonymousUser,
            isLoggedIn,
            isDebug,
            allowedAdminEmails,
            login,
            logout,
            userEmail,
            userId,
            userRole
        }}>
            {children}
        </AuthContext.Provider>
    );
};
